import { Grid, Typography } from '@mui/material'
import { ButtonV2, TextInputV4 } from '@labrav/react-components'
import { useStyles } from './BuyerNotificationForm.styles'
import React, { useCallback, useEffect, useState } from 'react'
import { FormikProps, FieldArray } from 'formik'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { get } from 'lodash'
import { getRandom } from '../../utils/helperFunctions'
import clsx from 'clsx'
import { OfferEndBuyers } from '../../../types/flyout'
import { RemoveRecipient } from '../OfferRecipientV3/OfferRecipientV3'

export const BuyerNotificationForm = React.memo(
  ({
    formik,
    isReadOnly = false,
    borderLess = false,
    deleteVisibeForAll,
  }: {
    formik: FormikProps<Record<string, unknown>>
    isReadOnly?: boolean
    borderLess?: boolean
    deleteVisibeForAll?: boolean
  }) => {
    const { values, errors, touched, handleBlur, handleChange, setFieldValue } =
      formik
    const { classes } = useStyles({ borderLess })
    const [randomKey, setRandomKey] = useState<number>(getRandom())
    const offerEndBuyers = (values.offerEndBuyers || []) as OfferEndBuyers[]

    const offerEndBuyersLength = offerEndBuyers?.length

    useEffect(() => {
      setRandomKey(getRandom())
    }, [offerEndBuyers.length])

    const formChangeHandler = useCallback(
      (keyName: string) =>
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          setFieldValue(keyName, event.target.value)
        },
      [setFieldValue]
    )

    return (
      <>
        <div
          className={clsx(
            classes.recipientsRowsContainer,
            borderLess ? classes.borderNone : ''
          )}
          data-testid="update-buyer"
        >
          <FieldArray name="offerEndBuyers">
            {({ push, remove }) => (
              <>
                {offerEndBuyers.map((offerEndBuyerItem, index) => (
                  <div
                    className={clsx(
                      classes.updateRow,
                      index === offerEndBuyersLength - 1
                        ? classes.lastRecipient
                        : '',
                      index === 0 ? classes.firstRecipient : '',
                      classes.borderRecipient
                    )}
                    data-testid={'buyer-row'}
                  >
                    <div className={classes.recipientTitleWrapper}>
                      <Typography className={classes.subTitle}>
                        Buyer {index + 1}
                      </Typography>
                      <div>
                        <RemoveRecipient
                          index={index}
                          recipientCount={offerEndBuyers.length}
                          remove={remove}
                          smallSize={true}
                          deleteVisibeForAll={deleteVisibeForAll}
                        />
                      </div>
                    </div>

                    <Grid container spacing={3} width={'100%'}>
                      <Grid
                        key={`offerEndBuyerItem-${randomKey}-firstName`}
                        size={{
                          md: 6,
                          lg: 6,
                          sm: 6,
                        }}
                      >
                        <TextInputV4
                          placeHolder=""
                          isRequired={false}
                          isReadOnly={isReadOnly}
                          keyName={`offerEndBuyers.${index}.firstName`}
                          onChange={formChangeHandler(
                            `offerEndBuyers.${index}.firstName`
                          )}
                          type="string"
                          title="First name"
                          value={offerEndBuyerItem.firstName || ''}
                          isError={
                            !!(
                              get(
                                touched,
                                `offerEndBuyers.${[index]}.firstName`,
                                false
                              ) &&
                              get(
                                errors,
                                `offerEndBuyers.${[index]}.firstName`,
                                false
                              )
                            )
                          }
                          errorText={
                            (!!get(
                              touched,
                              `offerEndBuyers.${[index]}.firstName`,
                              false
                            ) &&
                              get(
                                errors,
                                `offerEndBuyers.${[index]}.firstName`,
                                ''
                              )) ||
                            ''
                          }
                          onBlur={handleBlur}
                        />
                      </Grid>
                      <Grid
                        key={`offerEndBuyerItem-${randomKey}-lastName`}
                        size={{
                          md: 6,
                          lg: 6,
                          sm: 6,
                        }}
                      >
                        <TextInputV4
                          placeHolder=""
                          isRequired={false}
                          isReadOnly={isReadOnly}
                          keyName={`offerEndBuyers.${index}.lastName`}
                          onChange={formChangeHandler(
                            `offerEndBuyers.${index}.lastName`
                          )}
                          type="string"
                          title="Last name"
                          value={offerEndBuyerItem.lastName || ''}
                          isError={
                            !!(
                              get(
                                touched,
                                `offerEndBuyers.${[index]}.lastName`,
                                false
                              ) &&
                              get(
                                errors,
                                `offerEndBuyers.${[index]}.lastName`,
                                false
                              )
                            )
                          }
                          errorText={
                            (!!get(
                              touched,
                              `offerEndBuyers.${[index]}.lastName`,
                              false
                            ) &&
                              get(
                                errors,
                                `offerEndBuyers.${[index]}.lastName`,
                                ''
                              )) ||
                            ''
                          }
                          onBlur={handleBlur}
                        />
                      </Grid>
                      <Grid
                        key={`offerEndBuyerItem-${randomKey}-title`}
                        size={{
                          md: 6,
                          lg: 6,
                          sm: 6,
                        }}
                      >
                        <TextInputV4
                          placeHolder=""
                          isRequired={false}
                          isReadOnly={isReadOnly}
                          keyName={`offerEndBuyers.${index}.title`}
                          onChange={formChangeHandler(
                            `offerEndBuyers.${index}.title`
                          )}
                          type="string"
                          title="Title (Optional)"
                          value={offerEndBuyerItem.title || ''}
                          isError={
                            !!(
                              get(
                                touched,
                                `offerEndBuyers.${[index]}.title`,
                                false
                              ) &&
                              get(
                                errors,
                                `offerEndBuyers.${[index]}.title`,
                                false
                              )
                            )
                          }
                          errorText={
                            (!!get(
                              touched,
                              `offerEndBuyers.${[index]}.title`,
                              false
                            ) &&
                              get(
                                errors,
                                `offerEndBuyers.${[index]}.title`,
                                ''
                              )) ||
                            ''
                          }
                          onBlur={handleBlur}
                        />
                      </Grid>
                      <Grid
                        key={`offerEndBuyerItem-${randomKey}-email`}
                        size={{
                          md: 6,
                          lg: 6,
                          sm: 6,
                        }}
                      >
                        <TextInputV4
                          placeHolder=""
                          isRequired={false}
                          isReadOnly={isReadOnly}
                          keyName={`offerEndBuyers.${index}.email`}
                          onChange={handleChange}
                          type="string"
                          title="Email"
                          value={offerEndBuyerItem.email || ''}
                          isError={
                            !!(
                              get(
                                touched,
                                `offerEndBuyers.${[index]}.email`,
                                false
                              ) &&
                              get(
                                errors,
                                `offerEndBuyers.${[index]}.email`,
                                false
                              )
                            )
                          }
                          errorText={
                            (!!get(
                              touched,
                              `offerEndBuyers.${[index]}.email`,
                              false
                            ) &&
                              get(
                                errors,
                                `offerEndBuyers.${[index]}.email`,
                                ''
                              )) ||
                            ''
                          }
                          onBlur={handleBlur}
                        />
                      </Grid>
                    </Grid>
                  </div>
                ))}

                <ButtonV2
                  className={classes.addRecipientBtn}
                  data-testid={'add-a-buyer'}
                  onClick={() =>
                    push({
                      email: '',
                      firstName: '',
                      lastName: '',
                      title: '',
                    })
                  }
                  styleType="link"
                >
                  <FontAwesomeIcon
                    icon={faPlus}
                    width={'14px'}
                    height={'16px'}
                    style={{ paddingRight: '13px', paddingLeft: '5px' }}
                  />
                  Add buyer details
                </ButtonV2>
              </>
            )}
          </FieldArray>
        </div>
      </>
    )
  }
)
